import React, { useEffect, Children } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRightIcon, ClipboardCheckIcon, FactoryIcon, PackageIcon, FileTextIcon, ShoppingCartIcon, SettingsIcon, CogIcon, ShoppingBagIcon, FlaskConicalIcon } from 'lucide-react';
const fadeUp = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0
  }
};
const stagger = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.1
    }
  }
};
const modules = [{
  icon: ShoppingCartIcon,
  title: 'Purchasing',
  description: 'From requisition to receipt — streamline your entire procurement process so you get the right materials, from the right vendors, at the right time.',
  to: '/solutions/purchasing',
  image: 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?auto=format&fit=crop&q=80&w=800&h=400'
}, {
  icon: PackageIcon,
  title: 'Inventory Management',
  description: 'Know what you have, where it is, and when to reorder. Real-time visibility across every warehouse, bin, and lot.',
  to: '/solutions/inventory-management',
  image: 'https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?auto=format&fit=crop&q=80&w=800&h=400'
}, {
  icon: FactoryIcon,
  title: 'Production',
  description: "Real-time visibility from order release to shipment. Track production, capture data at every operation, and make decisions based on what's actually happening.",
  to: '/solutions/manufacturing-mes',
  image: 'https://images.unsplash.com/photo-1631541909061-71e349d1f203?auto=format&fit=crop&q=80&w=800&h=400'
}, {
  icon: ClipboardCheckIcon,
  title: 'Quality',
  description: 'From incoming inspection to final shipment — catch defects early, close CAPAs fast, and maintain compliance without the paperwork overhead.',
  to: '/solutions/quality',
  image: 'https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?auto=format&fit=crop&q=80&w=800&h=400'
}, {
  icon: FileTextIcon,
  title: 'Sales & Quoting',
  description: 'Build quotes from real cost data, convert to orders in one click, and track every deal from first inquiry to final invoice.',
  to: '/solutions/sales-and-quoting',
  image: 'https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?auto=format&fit=crop&q=80&w=800&h=400'
}, {
  icon: SettingsIcon,
  title: 'Custom Operating System',
  description: 'Your business is unique. Thruline builds software that adapts to you — every workflow, dashboard, and integration designed around how your team actually works.',
  to: '/solutions/custom',
  image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&q=80&w=800&h=400'
}];
const industries = [{
  icon: FactoryIcon,
  title: 'Manufacturing',
  description: 'From job shops to high-volume production, Thruline gives manufacturing teams the tools to plan, execute, and improve — without fighting their software.',
  to: '/industries/manufacturing',
  image: 'https://images.unsplash.com/photo-1565793298595-6a879b1d9492?auto=format&fit=crop&q=80&w=800&h=400'
}, {
  icon: CogIcon,
  title: 'Industrial Operations',
  description: "Whether you're managing field operations, facility maintenance, or multi-site logistics, Thruline builds the operational backbone your team needs.",
  to: '/industries/industrial-operations',
  image: 'https://images.unsplash.com/photo-1504328345606-18bbc8c9d7d1?auto=format&fit=crop&q=80&w=800&h=400'
}, {
  icon: ShoppingBagIcon,
  title: 'Complex Consumer Products',
  description: 'Consumer products companies deal with variants, regulations, and multi-channel complexity. Thruline builds the operational system that keeps it all connected.',
  to: '/industries/consumer-products',
  image: 'https://images.unsplash.com/photo-1556740758-90de374c12ad?auto=format&fit=crop&q=80&w=800&h=400'
}, {
  icon: FlaskConicalIcon,
  title: 'Biotech',
  description: 'From lab bench to commercial manufacturing, Thruline connects your LIMS, ELN, and operational data into a single system — so your team can focus on science, not spreadsheets.',
  to: '/industries/biotech',
  image: 'https://images.unsplash.com/photo-1532187863486-abf9dbad1b69?auto=format&fit=crop&q=80&w=800&h=400'
}];
function WideCard({
  item
}: {
  item: any;
}) {
  const Icon = item.icon;
  return <motion.div variants={fadeUp} className="w-full" data-id="element-1007">
      <Link to={item.to} className="group relative flex flex-col md:flex-row w-full overflow-hidden rounded-2xl border border-white/10 bg-white/[0.02] hover:bg-white/[0.04] hover:border-violet-electric/30 transition-all duration-300" data-id="element-1008">

        {/* Image Section */}
        <div className="w-full md:w-2/5 h-48 md:h-auto relative overflow-hidden" data-id="element-1009">
          <div className="absolute inset-0 bg-navy/20 group-hover:bg-transparent transition-colors duration-300 z-10" data-id="element-1010" />
          <img src={item.image} alt={item.title} className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-500" data-id="element-1011" />

          <div className="absolute inset-0 bg-gradient-to-r from-navy via-navy/50 to-transparent md:bg-gradient-to-l md:from-navy md:via-navy/20 md:to-transparent opacity-60" data-id="element-1012" />
        </div>

        {/* Content Section */}
        <div className="flex-1 p-8 md:p-10 flex flex-col justify-center relative z-20" data-id="element-1013">
          <div className="flex items-center justify-between mb-4" data-id="element-1014">
            <div className="w-12 h-12 rounded-xl bg-white/5 flex items-center justify-center group-hover:bg-violet-electric/20 transition-colors duration-300" data-id="element-1015">
              <Icon className="w-6 h-6 text-violet-electric" data-id="element-1016" />
            </div>
            <div className="w-8 h-8 rounded-full border border-white/10 flex items-center justify-center group-hover:bg-white group-hover:border-white transition-all duration-300" data-id="element-1017">
              <ArrowRightIcon className="w-4 h-4 text-white/40 group-hover:text-navy transition-colors duration-300" data-id="element-1018" />
            </div>
          </div>

          <h3 className="text-2xl font-bold text-white mb-3 group-hover:text-violet-electric transition-colors duration-300" data-id="element-1019">
            {item.title}
          </h3>
          <p className="text-white/60 text-lg leading-relaxed" data-id="element-1020">
            {item.description}
          </p>
        </div>
      </Link>
    </motion.div>;
}
export function SolutionsPage() {
  const location = useLocation();
  useEffect(() => {
    if (location.hash === '#industries') {
      setTimeout(() => {
        const el = document.getElementById('industries');
        if (el) {
          el.scrollIntoView({
            behavior: 'smooth'
          });
        }
      }, 100);
    } else {
      window.scrollTo(0, 0);
    }
  }, [location]);
  return <>
      {/* Hero */}
      <section id="hero" className="relative w-full pt-32 pb-20 px-6 overflow-hidden bg-navy" data-id="element-1021">

        <div className="absolute inset-0 overflow-hidden pointer-events-none" data-id="element-1022">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[700px] h-[500px] rounded-full animate-glow-pulse" style={{
          background: 'radial-gradient(ellipse at center, rgba(108, 92, 231, 0.2) 0%, rgba(200, 80, 192, 0.08) 40%, transparent 70%)'
        }} data-id="element-1023" />

        </div>

        <div className="relative z-10 max-w-3xl mx-auto text-center" data-id="element-1024">
          <motion.span className="text-violet-electric text-xs font-semibold tracking-[0.2em] uppercase block mb-4" initial={{
          opacity: 0,
          y: 12
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.4,
          delay: 0.1
        }} data-id="element-1025">

            Solutions
          </motion.span>

          <motion.h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-white leading-[1.1] tracking-tight mb-6" initial={{
          opacity: 0,
          y: 16
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: 0.15
        }} data-id="element-1026">

            Modules and industries
          </motion.h1>

          <motion.p className="text-lg text-white/55 max-w-2xl mx-auto leading-relaxed" initial={{
          opacity: 0,
          y: 16
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: 0.25
        }} data-id="element-1027">

            Operational modules built to work together. Each one is configured
            to match how your business actually runs — not how a generic system
            thinks it should.
          </motion.p>
        </div>

        <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-navy-light to-transparent pointer-events-none" data-id="element-1028" />
      </section>

      {/* Modules */}
      <section id="modules" className="relative w-full bg-navy-light py-24 px-6" data-id="element-1029">

        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-1/2 h-px bg-gradient-to-r from-transparent via-violet-electric/20 to-transparent" data-id="element-1030" />

        <div className="max-w-5xl mx-auto" data-id="element-1031">
          <motion.div className="mb-14" initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-60px'
        }} variants={fadeUp} transition={{
          duration: 0.5
        }} data-id="element-1032">

            <span className="text-violet-electric text-xs font-semibold tracking-[0.2em] uppercase block mb-4" data-id="element-1033">
              By Module
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-white tracking-tight" data-id="element-1034">
              Operational modules
            </h2>
          </motion.div>

          <motion.div className="space-y-6" variants={stagger} initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-40px'
        }} data-id="element-1035">

            {modules.map(mod => <WideCard key={mod.title} item={mod} data-id="element-1036" />)}
          </motion.div>
        </div>
      </section>

      {/* Industries */}
      <section id="industries" className="relative w-full bg-navy py-24 px-6" data-id="element-1037">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-1/2 h-px bg-gradient-to-r from-transparent via-white/[0.06] to-transparent" data-id="element-1038" />

        <div className="max-w-5xl mx-auto" data-id="element-1039">
          <motion.div className="mb-14" initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-60px'
        }} variants={fadeUp} transition={{
          duration: 0.5
        }} data-id="element-1040">

            <span className="text-violet-electric text-xs font-semibold tracking-[0.2em] uppercase block mb-4" data-id="element-1041">
              By Industry
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-white tracking-tight" data-id="element-1042">
              Industries we serve
            </h2>
          </motion.div>

          <motion.div className="space-y-6" variants={stagger} initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-40px'
        }} data-id="element-1043">

            {industries.map(ind => <WideCard key={ind.title} item={ind} data-id="element-1044" />)}
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section id="cta" className="relative w-full py-24 px-6 overflow-hidden" data-id="element-1045">
        <div className="absolute inset-0" style={{
        background: 'linear-gradient(135deg, #0D0B2E 0%, #1A1452 30%, #6C5CE7 70%, #C850C0 100%)'
      }} data-id="element-1046" />

        <motion.div className="relative z-10 max-w-3xl mx-auto text-center" initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-80px'
      }} variants={fadeUp} transition={{
        duration: 0.6
      }} data-id="element-1047">

          <h2 className="text-3xl sm:text-5xl font-bold text-white tracking-tight mb-6" data-id="element-1048">
            Not sure where to start?
          </h2>
          <p className="text-white/60 text-lg mb-10 max-w-xl mx-auto" data-id="element-1049">
            Discovery is free. Share your documents and we'll build a project
            plan that shows you exactly which modules fit your operations.
          </p>
          <a href="https://calendly.com/alex-thruline/learn-about-thruline" className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-white text-navy font-semibold text-sm hover:bg-white/90 transition-all hover:shadow-lg hover:shadow-white/10" data-id="element-1050">

            Start Free Discovery
            <ArrowRightIcon className="w-4 h-4" data-id="element-1051" />
          </a>
        </motion.div>
      </section>
    </>;
}